import { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectSettings } from '@/store/bootstrap/selectors';
import { WORKDAY_POSITION_ACTION_LABELS } from './constants';
import { useWorkdayPositionGate } from './useWorkdayPosition';
import { needsWorkdayPosition } from './workdayPositionVisibility';
import type { CreatePositionResult, WorkdayPosition } from './types';

interface IntegrationLike {
  type?: string;
  name?: string;
}

interface ApplicationLike {
  id?: string | number;
  job_id?: string | number;
  workday_position?: WorkdayPosition | null;
  contact?: { name?: string } | null;
  ats_job?: {
    id?: string | number;
    name?: string;
    job_profile?: { name?: string; job_title?: string } | null;
  } | null;
}

interface UseWorkdayPositionPushArgs {
  applicationData?: ApplicationLike | null;
  /** Refetch the application once a position has been created. */
  onPositionCreated?: () => void;
}

/**
 * How the Workday entry point should read, for hosts that only render the menu.
 *
 * Split from `useWorkdayPositionPush` because those hosts — `useGetActions` and
 * the HRIS submenu — render for every candidate row and own no drawer: giving
 * them the push hook meant carrying its open/closed state and drawer props for
 * nothing.
 */
export const useWorkdayPositionLabels = ({
  applicationData,
}: Pick<UseWorkdayPositionPushArgs, 'applicationData'> = {}) => {
  const settingsDetails = useSelector(selectSettings);
  const hrisIntegrations = useMemo(
    () =>
      settingsDetails?.account?.account_integrations?.hris_integrations?.filter(
        (integration: { status?: string }) => integration?.status === 'active'
      ) || [],
    [settingsDetails]
  );

  const { isEnabled } = useWorkdayPositionGate(hrisIntegrations);
  // `workday_position` now rides on list rows and every application response,
  // not just `show`, so every host can answer this. Its key is absent — not
  // null — when no position exists.
  const position = applicationData?.workday_position;
  const status = position?.status;

  /** True while Workday cannot accept an employee for this application yet. */
  const needsPosition = useCallback(
    (integrationType?: string) =>
      needsWorkdayPosition({ isEnabled, integrationType, position }),
    [isEnabled, position]
  );

  /**
   * Replacement label for the entry, or undefined to keep the host's default
   * ("Add to {name}").
   */
  const getActionLabel = useCallback(
    (integration?: IntegrationLike) => {
      if (!needsPosition(integration?.type)) return undefined;
      if (status === 'syncing') return WORKDAY_POSITION_ACTION_LABELS.syncing;
      // A failed position is offered as a plain create, not a "retry": the
      // reason was toasted at submit time and the action is identical.
      return WORKDAY_POSITION_ACTION_LABELS.create;
    },
    [needsPosition, status]
  );

  /** A syncing position offers nothing to click here. */
  const isActionDisabled = useCallback(
    (integrationType?: string) =>
      needsPosition(integrationType) && status === 'syncing',
    [needsPosition, status]
  );

  return { needsPosition, getActionLabel, isActionDisabled, status };
};

/**
 * Turns the generic "Add to Workday" entry points into the position flow while
 * no `ready` position exists. The host that owns the drawer calls
 * `interceptPush` before its own push and spreads `positionDrawerProps`; every
 * other integration passes through.
 */
export const useWorkdayPositionPush = ({
  applicationData,
  onPositionCreated,
}: UseWorkdayPositionPushArgs = {}) => {
  const { needsPosition, getActionLabel, isActionDisabled, status } =
    useWorkdayPositionLabels({ applicationData });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  /**
   * Call before pushing. A truthy result means the position flow has taken over
   * and the host must not run its own push — `'opened'` when the create drawer
   * is now showing, `'blocked'` when there is nothing to show. A host that
   * hides its own UI behind the drawer has to tell the two apart: hiding on
   * `'blocked'` leaves the user staring at nothing.
   */
  const interceptPush = useCallback(
    (integration?: IntegrationLike): 'opened' | 'blocked' | false => {
      if (!needsPosition(integration?.type)) return false;
      // Nothing to create and nothing to push while Kombo is still syncing —
      // the status card on the hired stage owns "Check again".
      if (status === 'syncing') return 'blocked';
      setIsDrawerOpen(true);
      return 'opened';
    },
    [needsPosition, status]
  );

  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  const handleCreated = useCallback(
    (_created: CreatePositionResult) => onPositionCreated?.(),
    [onPositionCreated]
  );

  return {
    interceptPush,
    getActionLabel,
    isActionDisabled,
    /** Spread onto `<CreatePositionDrawer />` at the host that owns the push. */
    positionDrawerProps: {
      isOpen: isDrawerOpen,
      onClose: closeDrawer,
      applicationId: applicationData?.id,
      jobTitle: applicationData?.ats_job?.name,
      jobId: applicationData?.job_id ?? applicationData?.ats_job?.id,
      // Usually absent — the embedded ats_job is trimmed — in which case the
      // hook fetches the job itself, sharing the employee form's cache.
      jobProfileName:
        applicationData?.ats_job?.job_profile?.name ??
        applicationData?.ats_job?.job_profile?.job_title,
      candidateName: applicationData?.contact?.name,
      onCreated: handleCreated,
    },
  };
};
